import { Box, Stack } from '@mui/material';
import {
  GridDensity,
  GridToolbarContainer
} from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import saveAs from 'file-saver';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, useContext, useEffect, useRef, useState } from 'react';
import { ToolbarActionGroup } from 'src/components/ui/actionGroup/actionGroupButtons';
import { CreateButton } from 'src/components/ui/actionGroup/CreateButton';
import { ExportButton } from 'src/components/ui/actionGroup/ExportButton';
import { FileImportButton } from 'src/components/ui/actionGroup/FileImportButton';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import { queryKey } from 'src/hooks/useAreas.request';

import MyGridToolbarColumnsButton from '../common/MyGridToolbarColumnsButton';
import MyGridToolbarDensitySelector from '../common/MyGridToolbarDensitySelector';
import FilterPanelToolbarFilterButton from '../common/MyGridToolbarFilterButton';

const CustomToolbar: FC<{
  toggleDialogIsOpen: () => void;
  handleDownloadCSV: () => void;
  setIsLoading: (prevState: boolean) => boolean;
  density: GridDensity;
  onDensityChange: (density: GridDensity) => void;
}> = memo(({
  toggleDialogIsOpen,
  handleDownloadCSV,
  setIsLoading,
  density,
  onDensityChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const licenseContext = useContext(LicenseContext);

  const importRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<File | undefined>(undefined);
  const [disableImport, setDisableImport] = useState<boolean>(false);
  const [importTooltip, setImportTooltip] = useState<string>('');

  useEffect(() => {
    if (licenseContext?.config?.selected_company_id === 0) {
      setDisableImport(true);
      setImportTooltip('インポート対象の事業所を選択してください');
    } else {
      setDisableImport(false);
      setImportTooltip('');
    }
  }, [licenseContext?.config]);

  const importButtonOnClick = (event: ChangeEvent<HTMLInputElement>) => {
    setImportFile(event.target.files[0]);
  };
  useEffect(() => {
    if (!importFile) return;

    const requestPath = '/api/v4/master/areas/import';

    const data = new FormData();
    data.append('file', importFile);

    setIsLoading(true);
    enqueueSnackbar('インポートを開始します');

    axios
      .post(requestPath, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(
        (response: AxiosResponse<{ severity: string; message: string }>) => {
          enqueueSnackbar(response.data.message);
        }
      )
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.status === 400) {
          enqueueSnackbar(
            '取り込めないエリア情報がありました、データをご確認ください。'
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            type: e.response.data.type
          });
          saveAs(blob, 'errors.txt');
        } else {
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
      })
      .finally(() => {
        setImportFile(undefined);
        setIsLoading(false);
        // eslint-disable-next-line no-void
        void queryClient.invalidateQueries([queryKey]);
        if (importRef) {
          importRef.current.value = '';
        }
      });
  }, [enqueueSnackbar, importFile, queryClient, setIsLoading]);

  return (
    <GridToolbarContainer
      sx={{
        width: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterPanelToolbarFilterButton />
          <MyGridToolbarColumnsButton />
          <MyGridToolbarDensitySelector
            density={density}
            onDensityChange={onDensityChange}
          />
        </Stack>
        <ToolbarActionGroup>
          <FileImportButton
            disabled={disableImport}
            tooltipTitle={importTooltip}
            onChange={importButtonOnClick}
            inputRef={importRef}
          />
          <ExportButton onClick={handleDownloadCSV} label="エクスポート" />
          <CreateButton onClick={toggleDialogIsOpen} label="エリア追加" />
        </ToolbarActionGroup>
      </Box>
    </GridToolbarContainer>
  );
});

export default CustomToolbar;
